import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { useWidget } from "/imports/state/WidgetContext";
import { OptionRow, AssignedResourceNote } from "/imports/ui/components/booking";
import { formatDate, formatMoney, formatDuration } from "/imports/ui/views/DashboardView/dashboardHelpers";
import { optionLabel } from "./recommendationsHelpers";

// Picker body shared by the mobile bottom-sheet and the desktop popover: a time
// header, an optional length picker, then the bookable resource rows.
function SlotPickerContent({ slot, currency, durations, durIdx, setDurIdx, active, options, noun, onChoose, allowResourcePick }) {
  const multiDuration = durations.length > 1;
  return (
    <>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 1, px: 0.5 }}>
        {slot.time} · {formatDate(slot.date)}
      </Typography>

      {/* Length picker (only with several lengths): each chip shows its own price and
          re-targets the resource rows below — no card clutter, choice on demand. */}
      {multiDuration && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
            Duration
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", px: 0.5 }}>
            {durations.map((d, i) => (
              <Chip
                key={d.pricingId || d.value}
                size="small"
                color={i === durIdx ? "primary" : "default"}
                variant={i === durIdx ? "filled" : "outlined"}
                label={
                  d.price != null
                    ? `${formatDuration(d.value)} · ${formatMoney(d.price, slot.currency || currency, { compact: true })}`
                    : formatDuration(d.value)
                }
                onClick={() => setDurIdx(i)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, overflowY: "auto" }}>
        {(!allowResourcePick || options.length > 1) && (
          // "Any" books the first (recommended) option — mirrors the booking flow.
          // With picking off it is the ONLY row, even for a lone option, whose
          // label would otherwise name the assigned court/trainer.
          <OptionRow
            label={`Any ${noun === "courts" ? "Court" : "Trainer"}`}
            sublabel="Recommended — best availability"
            selected
            onClick={() => onChoose(options[0], true)}
          />
        )}
        {allowResourcePick &&
          options.map((o, i) => (
            <OptionRow
              key={o.trainerId || o.facilityId || i}
              label={optionLabel(slot, o)}
              sublabel={active.price != null ? formatMoney(active.price, slot.currency || currency, { compact: true }) : undefined}
              // Naming a COURT is the client's own choice; naming a trainer is not
              // — a lesson's court is resolved server-side either way.
              onClick={() => onChoose(o, slot.category !== "court")}
            />
          ))}
      </Box>
      {!allowResourcePick && <AssignedResourceNote noun={noun === "courts" ? "Court" : "Trainer"} />}
    </>
  );
}

// fullWidth: fill the row on mobile (e.g. the stacked "Book again" hero) but keep
// the fixed 190px width from sm up and inside the horizontal-scroll category strips.
export default function RecommendationSlotCard({ slot, currency, onBook, fullWidth = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const { services } = useWidget();

  // Whether the club lets clients pick a specific court/trainer for this slot's
  // service; when off only "Any" is offered and it books the court the picker
  // chose. Absent flag = allowed (legacy services).
  const allowResourcePick = (services || []).find((s) => s._id === slot.serviceId)?.widgetAllowResourcePick !== false;

  // Nested durations (smallest-first); fall back to a single synthetic entry for
  // older payloads. Each entry carries its own price + bookable resources.
  const durations =
    Array.isArray(slot.durations) && slot.durations.length
      ? slot.durations
      : [{ value: slot.duration, pricingId: slot.pricingId, price: slot.price, options: slot.options || [] }];
  const multiDuration = durations.length > 1;
  // Length chosen inside the picker (default smallest); the card itself shows
  // only a "from" summary, so it stays uncluttered and easy to tap.
  const [durIdx, setDurIdx] = useState(0);
  const active = durations[Math.min(durIdx, durations.length - 1)];
  const options = active.options || [];
  const noun = slot.category === "court" ? "courts" : "trainers";

  // Book the selected duration: merge its length/price/resources onto the slot so
  // the cart item is exactly what the booking flow would produce for that length.
  // courtIsOurs: false only when the client tapped a named court row below.
  const book = (option, courtIsOurs = true) =>
    onBook({ ...slot, duration: active.value, pricingId: active.pricingId, price: active.price, options }, option, courtIsOurs);

  // Open the picker whenever there's a choice (a length and/or a resource);
  // book straight through only when there's exactly one of each. With picking
  // off the resource is never a choice, so several options still book straight
  // through rather than opening a picker whose only row is "Any".
  const hasChoice = multiDuration || (allowResourcePick && options.length > 1);
  const handleClick = (e) => {
    if (!hasChoice) {
      if (options[0]) book(options[0]);
      return;
    }
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);
  const choose = (option, courtIsOurs = true) => {
    close();
    book(option, courtIsOurs);
  };

  // Card summary: location + (single duration's resource, or count). With several
  // lengths the resources differ per length, so we keep the meta to the location.
  const resourceText = multiDuration ? null : options.length <= 1 ? (options[0] ? optionLabel(slot, options[0]) : null) : `${options.length} ${noun}`;
  const meta = [slot.locationName, resourceText].filter(Boolean).join(" · ");

  // Duration chip: a single length, or a compact range across the lengths.
  const durationLabel = multiDuration
    ? `${durations[0].value}–${durations[durations.length - 1].value} min`
    : active.value
      ? formatDuration(active.value)
      : null;

  // Price: exact for one length; "from <min>" when several lengths carry
  // different prices; a bare price when several lengths share one price.
  const prices = durations.map((d) => d.price).filter((p) => p != null);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const pricesVary = prices.length > 1 && Math.max(...prices) !== minPrice;
  const priceText =
    multiDuration && minPrice != null
      ? `${pricesVary ? "from " : ""}${formatMoney(minPrice, slot.currency || currency, { compact: true })}`
      : active.price != null
        ? formatMoney(active.price, slot.currency || currency, { compact: true })
        : null;

  const pickerContent = (
    <SlotPickerContent
      slot={slot}
      currency={currency}
      durations={durations}
      durIdx={durIdx}
      setDurIdx={setDurIdx}
      active={active}
      options={options}
      noun={noun}
      onChoose={choose}
      allowResourcePick={allowResourcePick}
    />
  );

  return (
    <>
      <Card
        variant="outlined"
        onClick={handleClick}
        sx={{
          ...(fullWidth
            ? { width: { xs: "100%", sm: 190 }, minWidth: { xs: 0, sm: 190 }, maxWidth: { xs: "100%", sm: 190 } }
            : { minWidth: "11.875rem", maxWidth: "11.875rem" }),
          flex: "0 0 auto",
          cursor: "pointer",
          transition: "border-color 120ms, box-shadow 120ms",
          "&:hover": { borderColor: "primary.main", boxShadow: 1 }
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="subtitle2" noWrap title={slot.serviceName}>
            {slot.serviceName}
          </Typography>
          {/* The date/time is the axis the user picks along — it gets the card's
              single accent; price stays neutral so the two don't compete. */}
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {formatDate(slot.date)} · {slot.time}
          </Typography>
          {meta && (
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.25 }} noWrap title={meta}>
              {meta}
            </Typography>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={0.75}>
            {durationLabel ? <Chip size="small" variant="outlined" label={durationLabel} sx={{ height: "1.25rem", fontSize: "0.65rem" }} /> : <span />}
            {priceText && (
              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                {priceText}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
      {hasChoice &&
        // Mobile: bottom sheet (matches the booking flow's option picker).
        // Desktop: popover anchored to the card.
        (isMobile ? (
          <Drawer
            anchor="bottom"
            open={!!anchorEl}
            onClose={close}
            PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "75vh", px: 2.5, pt: 2, pb: 3 } }}
          >
            <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
            {pickerContent}
          </Drawer>
        ) : (
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={close}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { p: 1.5, borderRadius: 2.5, width: "16.25rem", maxHeight: "22.5rem", boxShadow: "0 8px 30px rgba(26,26,26,0.16)" } }}
          >
            {pickerContent}
          </Popover>
        ))}
    </>
  );
}
